import { Response } from '@types';
import { call, put, retry, takeLatest } from 'redux-saga/effects';

import {
  getAutocompleteOptions,
  getDomainTables,
  getGuideData,
  getGuidePDFUrl,
  getHiredSolicitantData,
  patchGuideSADT
} from 'features/tissInvoicing/services/sadt';
import {
  ActionKeys,
  Autocomplete,
  DomainTables,
  GeneratePDFResponse,
  GuideSADT,
  HiredSolicitantCodeEntry
} from 'features/tissInvoicing/types';
import getSnackbarPayload from 'features/tissInvoicing/utils/getSnackbarPayload';
import { captureException } from 'shared/utils/handlerErrors';
import { actions } from '.';
import { RETRY_DELAY_MS, RETRY_TIMES } from '../constants';
import { normalizeGuideSADTPatchPayload } from './normalizers';

type SaveGuideAction = ReturnType<typeof actions.saveGuide>;
type PrintGuideAction = ReturnType<typeof actions.printGuide>;
type FetchGuideDataAction = ReturnType<typeof actions.fetchGuideData>;
type FetchAutocompleteOptionsAction = ReturnType<
  typeof actions.fetchAutocompleteOptions
>;
type FetchHiredSolicitantData = ReturnType<
  typeof actions.fetchHiredSolicitantData
>;

export function* fetchGuideDataWorker({
  payload: { eventId },
}: FetchGuideDataAction) {
  try {
    yield put(
      actions.setLoadingStatus({ key: ActionKeys.FetchGuide, value: true }),
    );

    const { data, status }: Response<GuideSADT> = yield retry(
      RETRY_TIMES,
      RETRY_DELAY_MS,
      getGuideData,
      eventId,
    );

    if (status === 201)
      yield put(
        actions.setSnackbarContent(
          getSnackbarPayload('Guia gerada com sucesso', 'success'),
        ),
      );

    yield put(actions.setGuideData(data));
  } catch (error) {
    captureException(error);
  } finally {
    yield put(
      actions.setLoadingStatus({ key: ActionKeys.FetchGuide, value: false }),
    );
  }
}

export function* fetchDomainTablesWorker() {
  try {
    yield put(
      actions.setLoadingStatus({
        key: ActionKeys.FetchDomainTables,
        value: true,
      }),
    );

    const { data }: Response<DomainTables> = yield retry(
      RETRY_TIMES,
      RETRY_DELAY_MS,
      getDomainTables,
    );

    yield put(actions.setDomainTables(data));
  } catch (error) {
    captureException(error);
  } finally {
    yield put(
      actions.setLoadingStatus({
        key: ActionKeys.FetchDomainTables,
        value: false,
      }),
    );
  }
}

export function* fetchAutocompleteOptionsWorker({
  payload: { eventId },
}: FetchAutocompleteOptionsAction) {
  try {
    yield put(
      actions.setLoadingStatus({
        key: ActionKeys.FetchAutocomplete,
        value: true,
      }),
    );

    const {
      data,
    }: Response<Omit<Autocomplete, 'hired_solicitants'>> = yield retry(
      RETRY_TIMES,
      RETRY_DELAY_MS,
      getAutocompleteOptions,
      eventId,
    );

    yield put(actions.setAutocompleteOptions(data));
  } catch (error) {
    captureException(error);
  } finally {
    yield put(
      actions.setLoadingStatus({
        key: ActionKeys.FetchAutocomplete,
        value: false,
      }),
    );
  }
}

export function* fetchHiredSolicitantDataWorker({
  payload: { eventId, solicitantId, type, callback },
}: FetchHiredSolicitantData) {
  try {
    const { data }: Response<HiredSolicitantCodeEntry> = yield retry(
      RETRY_TIMES,
      RETRY_DELAY_MS,
      getHiredSolicitantData,
      eventId,
      solicitantId,
      type,
    );

    yield put(actions.setHiredSolicitants(data));

    if (callback) callback(data);
  } catch (error) {
    captureException(error);
  }
}

export function* saveGuideWorker({
  payload: { guideId, data },
}: SaveGuideAction) {
  try {
    yield put(
      actions.setLoadingStatus({
        key: ActionKeys.Save,
        value: true,
      }),
    );

    const normalizedPayload = normalizeGuideSADTPatchPayload(data);
    const { data: guide }: Response<GuideSADT> = yield call(
      patchGuideSADT,
      guideId,
      normalizedPayload,
    );

    yield put(actions.setGuideData(guide));
    yield put(
      actions.setSnackbarContent(
        getSnackbarPayload('Guia salva com sucesso', 'success'),
      ),
    );
  } catch (error) {
    yield put(
      actions.setSnackbarContent(
        getSnackbarPayload('Houve um problema ao salvar a guia', 'error'),
      ),
    );
    captureException(error);
  } finally {
    yield put(
      actions.setLoadingStatus({
        key: ActionKeys.Save,
        value: false,
      }),
    );
  }
}

export function* printSadtFormWorker({
  payload: { guideId, eventId, data },
}: PrintGuideAction) {
  try {
    yield put(
      actions.setLoadingStatus({
        key: ActionKeys.Print,
        value: true,
      }),
    );
    yield put(actions.setGuidePDFUrl(''));

    // Salvar guia SP/SADT
    try {
      const normalizedPayload = normalizeGuideSADTPatchPayload(data);
      const { data: guide }: Response<GuideSADT> = yield call(
        patchGuideSADT,
        guideId,
        normalizedPayload,
      );

      yield put(actions.setGuideData(guide));
    } catch {
      throw new Error('Houve um problema ao salvar a guia');
    }

    // Gerar PDF da guia SP/SADT
    try {
      const {
        data: { url },
      }: Response<GeneratePDFResponse> = yield call(getGuidePDFUrl, eventId);
      yield put(actions.setGuidePDFUrl(url));
      yield put(actions.setPrintModalOpen(true));
    } catch {
      throw new Error('Houve um problema ao imprimir a guia');
    }
  } catch (error) {
    if (error instanceof Error) {
      yield put(
        actions.setSnackbarContent(getSnackbarPayload(error?.message, 'error')),
      );
      captureException(error);
    }
  } finally {
    yield put(
      actions.setLoadingStatus({
        key: ActionKeys.Print,
        value: false,
      }),
    );
  }
}

export default function* watchSadtWorker() {
  yield takeLatest(actions.fetchGuideData, fetchGuideDataWorker);
  yield takeLatest(
    actions.fetchAutocompleteOptions,
    fetchAutocompleteOptionsWorker,
  );
  yield takeLatest(
    actions.fetchHiredSolicitantData,
    fetchHiredSolicitantDataWorker,
  );
  yield takeLatest(actions.fetchDomainTables, fetchDomainTablesWorker);
  yield takeLatest(actions.saveGuide, saveGuideWorker);
  yield takeLatest(actions.printGuide, printSadtFormWorker);
}
